import { errorAttributesOf } from '@/enums/errorAttributes'
import { mintOperationId } from '@/libs/operationId'
import { createOperationNames, operationVerdict, type OperationVerdict } from '@/libs/operationCore'
import type { ApiAnswer } from '@/stores/fetch'

/**
 * What one answer to an idempotent press means, in this portal.
 *
 * # Why this file is smaller here than in the other portals
 *
 * Elsewhere this is `useOperation()`: a Map of the names of the presses a screen is
 * waiting for, and a `run()` that spends or keeps each one. This portal mints no names at
 * all. The one operation a guest presses is one the SERVICE named — a card Milo proposed,
 * confirmed or declined by its own id (docs/AI.md §24) — so there is nothing here to hold
 * and nothing to spend, and a Map nobody reads would be a mechanism kept warm for a
 * caller that does not exist.
 *
 * What is needed is the other half of the rule, and it is the half this portal was
 * getting wrong: whether an operation is OVER, and therefore whether the thread has to be
 * read again before the guest presses anything else. That came out of two error codes
 * written into the chat store by hand.
 *
 * What every portal shares is `libs/operationCore.ts`, which is the same file in all of
 * them and is what actually decides.
 *
 * # The presses this portal names itself
 *
 * Two arrived with the guest's own services (docs/PLAN_GOST.md): the room-service order
 * and a partner's slot. Both are a button pressed on a telephone that may lose its answer
 * in a lift, and both reach something that cannot be taken back — a till's bill, a
 * partner's diary — so each carries a name the service turns into one act however many
 * times it arrives. They get `useOperation()` below, which is `pms-web`'s copied rather
 * than invented again; the only difference is the shape of the answer it reads, because
 * this portal's transport answers `{ status, data }` rather than two refs.
 *
 * # Nobody reads a code to decide this
 *
 * The service answers every refusal with `retryable` and `operation_closed` beside
 * `code` and `message`, and those two are preferred over everything. Where a body
 * carries neither — an older service behind the edge during a deploy — the generated
 * `enums/errorAttributes.ts` answers out of the same table, written from
 * `pkg/enums/error/main.go` by a Go test.
 *
 * The chat store goes on choosing the SENTENCE by code — „kartica je potekla“ against
 * „o njej je že bilo odločeno“ — and that is a different question, unaffected. What may
 * not be decided by a code is whether to press again and whether to look again.
 */

/**
 * The verdict on one answer: `done`, `closed`, `retry` or `refused`.
 *
 * `statusCode` is `null` or `0` where nothing of ours came back at all — a telephone that
 * lost its signal mid-press, a request that never left — which answers `retry`: the same
 * card may be decided again, and the service's own claim is what makes that safe.
 */
export const answerVerdict = (statusCode: number | null, data: unknown): OperationVerdict =>
    operationVerdict(statusCode, data, errorAttributesOf)

export interface IOperationOutcome<T> {
    /** It worked. */
    ok: boolean
    /** The operation is over and did not do what was asked: re-read before offering it again. */
    closed: boolean
    /** The same press may simply be made again. */
    retryable: boolean
    verdict:   OperationVerdict
    status:    number
    /** The answer on success, the refusal otherwise. */
    data: T | null
}

export interface IOperation {
    run: <T>(key: string, send: (operationId: string) => Promise<ApiAnswer<T>>) => Promise<IOperationOutcome<T>>
    /** Drops every name — what a screen calls when the content its presses are about changes. */
    forgetAll: () => void
}

/**
 * One screen's named presses — `pms-web`'s `useOperation()` over this portal's transport.
 *
 * `key` names WHAT is being done closely enough that two different acts cannot share it:
 * the basket and the note, the partner's service and the start. While the key is the same
 * the name is the same, which is what lets the service answer a retry with the first
 * attempt's result instead of cooking a second supper.
 */
export const useOperation = (): IOperation => {
    const names = createOperationNames()

    const run = async <T>(key: string, send: (operationId: string) => Promise<ApiAnswer<T>>): Promise<IOperationOutcome<T>> => {
        const operationId = names.nameFor(key, mintOperationId)

        // The transport never throws (stores/fetch.ts): no signal is status 0, which the
        // core reads as `retry`, and the name is kept for exactly that reason.
        const answer  = await send(operationId)
        const verdict = operationVerdict(answer.status, answer.data, errorAttributesOf)

        names.settle(key, verdict)

        return {
            ok:        verdict === 'done',
            closed:    verdict === 'closed',
            retryable: verdict === 'retry',
            verdict,
            status: answer.status,
            data:   answer.data,
        }
    }

    return { run, forgetAll: names.forgetAll }
}
